import {  useEffect, useImperativeHandle, useRef, useState } from "react";
import { Card } from "./Card";
import { isArrayEmpty, isNullOrEmpty, isNumeric } from "../../helpers/ObjectHelpers";
import { Button, ButtonGroup, Toolbar, ToolbarSeparator } from "@progress/kendo-react-buttons";
import { CheckSquare, SidebarSimple, Square } from "phosphor-react-sc";
import { NoContentMessage } from "../NoContentMessage";
import parse from 'html-react-parser';
import { useClassObserver } from "../../hooks/useClassObserver";
import { CardListProps } from "./props/CardListProps";
import { ICardListDispatcherService } from "./services/ICardListDispatcherService";
import CardListDispatcherService from "./services/CardListDispatcherService";
import { CardListItemProps } from "./props/CardListItemProps";
import { useNavigate, useSearchParams } from "react-router";
import { scrollCardElementIntoView } from "../../helpers/ElementHelpers";

const headerHeightWithMargin = 70 + 20;

export const CardList = ({
    ref,
    source = null,
    showTools = false,
    noContentMessage = "Det er ikke registrert noe data",
    busyMessage = "Laster inn data...",
    onReset,
    sortDirection = "asc",
    searchParamName = "",
    disabled = false,
    canShowNoContentMessage = true,
    isBusy = false
}: CardListProps) => {

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useImperativeHandle(ref, () => ({
        onDeleted(deletedIds: number[]) {
            dispatcherService.onDeleted(deletedIds);
        },
        onAdded(item: CardListItemProps) {
            if (isArrayEmpty(localSource)) {
                localSource.push(item);
            } else {
                const exists = localSource.some(x => x.id === item.id);
                if (item.id && exists) {
                    dispatcherService.onUpdated(item, sortDirection);
                } else {
                    item.added = true;

                    const sortValues: any[] = localSource.map(x => ({ id: x.id, sortValue: x.sortValue }));
                    const newSortValue = { id: item.id, sortValue: item.sortValue };

                    sortValues.push(newSortValue);

                    const index = dispatcherService.findIndex(sortDirection, sortValues, item);
                    localSource.splice(index, 0, item);
                }
            }
        },
        onReset(id: number) {
            dispatcherService.onReset(id);
        }
    }));

    const refs = useRef<any[]>([]);
    const [isSelectActive, setIsSelectActive] = useState(false);
    const [localSource, setLocalSource] = useState<CardListItemProps[]>([]);

    const currentRef: {
        current: {
            ptrBreached: boolean
        }
    } = useRef({
        ptrBreached: false,
        maxSelectable: 0,
    });
    const { current: lr } = currentRef;

    const dispatcherServiceRef = useRef<ICardListDispatcherService | null>(null);
    if (!dispatcherServiceRef.current) {
        dispatcherServiceRef.current = new CardListDispatcherService(
            onReset,
            showTools,
            setIsSelectActive,
            setLocalSource,
        );
    }

    const dispatcherService = dispatcherServiceRef.current;

    useClassObserver(".ptr", "ptr--pull-down-treshold-breached", () => {
        if (!lr.ptrBreached) {
            lr.ptrBreached = true;
            dispatcherService.onToggleItems(false);
        }
    });

    useEffect(() => {
        if (!source) return;
        lr.ptrBreached = false;
        setLocalSource(source);
    }, [source]);

      useEffect(() => {
        if (isNullOrEmpty(searchParamName)) return;

        const paramValue = searchParams.get(searchParamName);
        if (isNumeric(paramValue)) {
            scrollCardElementIntoView(
                paramValue
            );
            
            const params = new URLSearchParams(searchParams);
            params.delete(searchParamName);
            navigate({ search: params.toString() }, { replace: false });
        }

    }, [localSource]);

    useEffect(() => {
        if (!dispatcherService) return;
        dispatcherService.source = localSource;
    }, [localSource, dispatcherService]);

    useEffect(() => {
        if (!dispatcherService) return;
        dispatcherService.refs = refs;
    }, [refs, dispatcherService]);

    useEffect(() => {
        return (() => {
            refs.current = [];
            dispatcherServiceRef.current = null;
        })
    }, []);
    
    return (
        (isBusy) ?
            <NoContentMessage message={parse(busyMessage)} />
            :
            (isArrayEmpty(localSource)) ?
                (canShowNoContentMessage) &&
                    <NoContentMessage message={parse(noContentMessage)} />
                :
                <div className="crds">
                    {
                        showTools &&
                        <Toolbar>
                            <Button
                                togglable={true}
                                selected={isSelectActive}
                                className="k-toolbar-button"
                                onClick={() => {
                                    const active = !isSelectActive;
                                    setIsSelectActive(active);
                                    dispatcherService.onToggleAll(active);
                                }}>
                                <SidebarSimple size={24} weight="duotone" />
                            </Button>

                            <ToolbarSeparator />
                            <ButtonGroup disabled={!isSelectActive}>
                                <Button
                                    className="k-toolbar-button"
                                        onClick={() => dispatcherService.onSelectAll(true)}>
                                    <CheckSquare size={24} weight="duotone" />
                                </Button>
                                <Button
                                    className="k-toolbar-button"
                                        onClick={() => dispatcherService.onSelectAll(false)}>
                                    <Square size={24} weight="duotone" />
                                </Button>
                            </ButtonGroup>
                        </Toolbar>
                    }
                    {
                       
                         localSource.map((item, index) =>
                            <Card
                                key={item.key}
                                ref={(e: any) => (refs.current[index] = e)}
                                id={item.id}
                                title={item.title}
                                subTitle={item.subTitle}
                                subTitleClassName={item.subTitleClassName}
                                borderColor={item.borderColor}
                                backgroundColor={item.backgroundColor}
                                indicatorPrimaryColor={item.indicatorPrimaryColor}
                                indicatorSecondaryColor={item.indicatorSecondaryColor}
                                showIndicator={item.showIndicator}
                                actions={item.actions}
                                confirmDelete={item.confirmDelete}
                                confirmDeleteTitle={item.confirmDeleteTitle}
                                confirmDeleteText={item.confirmDeleteText}
                                swipeMode={item.swipeMode}
                                icons={item.icons}
                                canDelete={item.canDelete}
                                onDelete={item.onDelete}
                                deleted={item.deleted}
                                added={item.added} 
                                onClick={item.onClick}
                                pinSelect={item.pinSelect}
                                selected={item.selected}
                                canSelect={item.canSelect}
                                systemName={item.systemName}
                                showSelect={item.showSelect }
                                allowBothSelectAndClick={item.allowBothSelectAndClick}
                                onSelect={(id: number, selected: boolean) => dispatcherService.onSelect(id, selected)}
                                onToggleSelect={() => dispatcherService.onToggleSelect()}
                                onActivateSelect={(id: number) => dispatcherService.onActivateSelect(id)}
                                onDeactivateSelect={(id: number) => dispatcherService.onDeactivateSelect(id)}>
                                {item.content}
                            </Card>
                        )
                    }
                </div>
    )
}
